import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { CustomButton } from '@medtronic/pecc-react-component-library-js';
import { Box, FormControlLabel, Grid, Modal, TextField } from '@mui/material';
import { EmrQuery } from '~/api/v2/endpoints/emr';
import { useCreateEmrMutation, useEditEmrMutation, useGetEmrsQuery, useGetProjectQuery } from '~/api/v2/hooks';
import { ModalHeader } from '~/components';
import { modalActionBlockStyles, modalContainerStyle } from '~/components/styles';
import { useAppDispatch } from '~/redux';
import { closeModalById } from '~/redux/slices/modalSlice';
import { emrStudioSchema } from '~/validation';
import { MAX_DESCRIPTION_LENGTH, studioNameRules } from '~/validation/emrStudioSchema';

const CreateEmrStudioModal = ({ id }: { id: string }) => {
  const dispatch = useAppDispatch();
  const projectId = +useParams().projectid!;
  const { data: emrInfo } = useGetEmrsQuery({ projectId }, { skip: !projectId });
  const [createEmr] = useCreateEmrMutation({
    fixedCacheKey: `create-emr-project-${projectId}`,
  });
  const [editEmr] = useEditEmrMutation({
    fixedCacheKey: `edit-emr-project-${projectId}`,
  });
  const { data: project } = useGetProjectQuery({ projectId }, { skip: !projectId });

  const {
    register,
    handleSubmit,
    reset,
    formState: { isValid, defaultValues, isDirty, errors },
  } = useForm<Omit<EmrQuery, 'projectId' | 'emrId'>>({
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(emrStudioSchema),
  });

  useEffect(() => {
    if (emrInfo) {
      reset({
        name: emrInfo.name || '',
        description: emrInfo.description || '',
        metadata: {},
      });
    }
  }, [emrInfo, reset]);

  const handleClose = () => {
    reset();
    dispatch(closeModalById(id));
  };

  const onSubmit = (data: Omit<EmrQuery, 'projectId' | 'emrId'>) => {
    if (defaultValues?.name) {
      editEmr({ projectId, emrId: emrInfo?.id || 0, ...data });
    } else {
      createEmr({ projectId, ...data });
    }
    handleClose();
  };

  return (
    <Modal open onClose={handleClose}>
      <Box tabIndex={-1} sx={modalContainerStyle}>
        <ModalHeader
          onClose={handleClose}
          title={[
            emrInfo ? 'Edit' : 'Create' + ' Studio',
            `${emrInfo ? 'S' : 'Create s'}tudio for project “${project?.name}“`,
          ]}
        />
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={4} p={2} pb={4}>
            <Grid size={12}>
              <FormControlLabel
                required
                label="STUDIO NAME"
                control={
                  <TextField
                    {...register('name')}
                    placeholder="Enter the Studio Name"
                    error={!!errors.name}
                    helperText={
                      errors.name?.message ||
                      `Use up to ${studioNameRules.max} characters. Only alphanumeric characters, underscore, and hyphen are allowed.`
                    }
                    slotProps={{ htmlInput: { maxLength: studioNameRules.max } }}
                  />
                }
              />
            </Grid>
            <Grid size={12}>
              <FormControlLabel
                label="DESCRIPTION"
                control={
                  <TextField
                    {...register('description')}
                    placeholder="Enter the Description"
                    error={!!errors.description}
                    helperText={errors.description?.message || `Use up to ${MAX_DESCRIPTION_LENGTH} characters`}
                    multiline
                    rows={3}
                    slotProps={{ htmlInput: { maxLength: MAX_DESCRIPTION_LENGTH } }}
                  />
                }
              />
            </Grid>
          </Grid>
          <Box sx={modalActionBlockStyles}>
            <CustomButton buttonType="borderless" label="Cancel" onClick={handleClose} />
            <CustomButton label={emrInfo ? 'Edit' : 'Create'} type="submit" disabled={!(isValid && isDirty)} />
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

export default CreateEmrStudioModal;
